import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';

import { Box, Icon, Image, ScrollView, View } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { Typography } from 'ucl/components/text/Typography';

import Currency from 'components/currency';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { toaster } from 'components/toaster/toaster';
import { KeyboardAwareView } from 'components/ucl/keyboard-aware-view';
import {
  BackgroundAndLogoContainer,
  GiftCardContainer,
  LogoAndNameContainer,
} from 'experiments/checkout-v2/treatment/features/payment/components/add-reload-prepaid-method-modal/styled';
import { TransferBalanceContainer } from 'experiments/checkout-v2/treatment/features/payment/components/transfer-balance';
import { useCardNumber } from 'experiments/checkout-v2/treatment/features/payment/hooks/use-giftcard-number';
import { AddGiftCardInput } from 'experiments/checkout-v2/treatment/pages/cart/payment/add-gift-card-input';
import { usePrepaidByNumber } from 'hooks/prepaid';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { toError } from 'utils/errors';

import { AddEditMethodProps } from './types';

const crownsImg = require('styles/images/crowns-image.png');

export type ReloadMethodRef = {
  reloadCard: () => void;
};
export const ReloadPrepaidMethod = forwardRef<ReloadMethodRef, AddEditMethodProps>(
  ({ openErrorDialog, isLoading, setIsLoading }, ref) => {
    const {
      setIsAddReloadPrepaidMethodModalVisible,
      getPrepaidPaymentMethod,
      setCheckoutPaymentMethodId,
      setDefaultPaymentMethodId,
      mergePrepaidCardBalances,
      getBalanceFromPaymentMethods,
    } = usePaymentContext();
    const {
      giftCardNumber,
      errors,
      handleCardNumberChange,
      isValidGiftCardNumberCheck,
      validateCardNumber,
    } = useCardNumber();
    const { getPrepaidByNumber } = usePrepaidByNumber();

    const [cardBalance, setCardBalance] = useState(0);
    const cardNumber = giftCardNumber?.replace(/\s+/g, '');
    const showBalance = !isLoading && !errors.giftCardNumber && cardBalance > 0;
    const paymentMethod: IPaymentMethod | null = getPrepaidPaymentMethod();
    const currentAmount = (paymentMethod && getBalanceFromPaymentMethods(paymentMethod)) || 0;
    const [oldGiftCardNumber, setOldGiftCardNumber] = useState('');

    const showGiftCardAddingErrorModal = useCallback(
      (error: unknown) => {
        openErrorDialog({ message: t('giftCardAddingError'), error: toError(error) });
      },
      [openErrorDialog]
    );

    const transferCard = useCallback(async () => {
      if (cardNumber) {
        const { data } = await getPrepaidByNumber({ cardNumber });
        if (!data) {
          showGiftCardAddingErrorModal(errors);
          setCardBalance(0);
        } else {
          if (data?.balance === 0) {
            openErrorDialog({ message: t('giftCardAlreadyRedeem') });
          } else {
            setCardBalance(data?.balance || 0);
          }
        }
        setIsLoading(false);
      }
    }, [
      cardNumber,
      errors,
      getPrepaidByNumber,
      openErrorDialog,
      setIsLoading,
      showGiftCardAddingErrorModal,
    ]);

    const handleGiftCardChange = useCallback(async () => {
      setIsLoading(true);
      handleCardNumberChange(cardNumber);
      validateCardNumber(cardNumber);

      if (isValidGiftCardNumberCheck(cardNumber)) {
        await transferCard();
      } else if (errors) {
        setIsLoading(false);
      }
    }, [
      cardNumber,
      errors,
      handleCardNumberChange,
      isValidGiftCardNumberCheck,
      setIsLoading,
      transferCard,
      validateCardNumber,
    ]);

    const confirmTransferCard = useCallback(async () => {
      try {
        if (!errors.giftCardNumber && cardBalance > 0) {
          setIsLoading(true);
          await mergePrepaidCardBalances({
            sourceCardNumber: giftCardNumber || '',
          });
          // Setting Default Payment method
          setCheckoutPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_GIFT_CARD);
          setDefaultPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_GIFT_CARD);

          toaster.positive(t('transferBalanceCompletedMessage'));
          setIsAddReloadPrepaidMethodModalVisible(false);
        }
      } catch (error) {
        openErrorDialog({ message: t('giftCardAddingError'), error: toError(error) });
      } finally {
        setIsLoading(false);
      }
    }, [
      cardBalance,
      errors.giftCardNumber,
      giftCardNumber,
      mergePrepaidCardBalances,
      openErrorDialog,
      setCheckoutPaymentMethodId,
      setDefaultPaymentMethodId,
      setIsAddReloadPrepaidMethodModalVisible,
      setIsLoading,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        reloadCard: async () => {
          if (showBalance) {
            await confirmTransferCard();
          } else {
            setOldGiftCardNumber(giftCardNumber);
            await handleGiftCardChange();
          }
        },
      }),
      [confirmTransferCard, giftCardNumber, handleGiftCardChange, showBalance]
    );

    return (
      <>
        <ScrollView>
          <GiftCardContainer>
            <BackgroundAndLogoContainer>
              <LogoAndNameContainer>
                <Icon accessibilityHidden variant="giftCard" size="xl" mb="$1" />
                <Typography
                  accessible
                  accessibilityRole="header"
                  accessibilityLabel={t('transferGiftCard')}
                  color={primitive.$bbqBrown}
                  fontFamily="heading"
                  fontSize="2xl"
                  mb="$2"
                  lineHeight={24}
                >
                  {t('giftCard')}
                </Typography>
                <Box width="full" flexDirection="row" mb="$1">
                  <Typography fontSize="sm">{t('currentBalance')}</Typography>

                  <Currency
                    amount={currentAmount}
                    textProps={{
                      fontSize: 'sm',
                      style: { margin: 0 },
                    }}
                  />
                </Box>
                {showBalance && (
                  <Box width="full" flexDirection="row" mt="$1">
                    <Typography fontSize="sm" bold>
                      {t('newBalance')}
                    </Typography>

                    <Currency
                      amount={currentAmount + cardBalance}
                      textProps={{
                        fontSize: 'sm',
                        style: { margin: 0 },
                      }}
                      isBold
                    />
                  </Box>
                )}
              </LogoAndNameContainer>
              <Image source={crownsImg} width={162} height={145} marginTop="$7" />
            </BackgroundAndLogoContainer>
          </GiftCardContainer>

          {!showBalance && (
            <KeyboardAwareView withScroll>
              <View style={{ paddingHorizontal: spacing.$4 }}>
                <AddGiftCardInput
                  onChange={handleCardNumberChange}
                  errorMessage={errors?.giftCardNumber}
                  label={t('cardNumber')}
                  testID="giftcard-input"
                  initialValue={oldGiftCardNumber}
                  isRedesign={true}
                  isLoading={isLoading}
                />
              </View>
            </KeyboardAwareView>
          )}
          {showBalance && (
            <TransferBalanceContainer
              cardBalance={cardBalance}
              giftCardNumber={giftCardNumber}
              customStyle={{ width: '100%' }}
            />
          )}
        </ScrollView>
      </>
    );
  }
);
